/**
 * 自动曝光扫描器
 * 基于事件驱动（scroll/resize/click/路由变化）自动发现并注册 data-tracker-ele-id 元素
 * 不使用 MutationObserver，每次扫描时通过 element.isConnected 清理已移除元素
 */

import { DATA_ATTRS, AUTO_EXPOSURE_DEFAULTS } from '../core/constants'
import type { RouteChangeManager, RouteChangeInfo, Unsubscribe } from '../core/route-change-manager'

// ── 类型定义 ──

/**
 * 扫描性能指标
 */
export interface ScanMetrics {
  /** 总扫描次数 */
  totalScans: number
  /** 累计发现元素数 */
  totalElementsDiscovered: number
  /** 当前正在观察的元素数 */
  currentObservedCount: number
  /** 最近一次扫描耗时（ms） */
  lastScanDuration: number
  /** 平均扫描耗时（ms） */
  avgScanDuration: number
  /** 最大扫描耗时（ms） */
  maxScanDuration: number
  /** 各触发源的触发次数 */
  scanTriggerCounts: Record<string, number>
  /** 被节流丢弃的扫描次数 */
  droppedByThrottle: number
  /** 因容量限制被跳过的元素数 */
  droppedByCapacity: number
  /** 清理的已移除元素数 */
  removedByCleanup: number
  /** 扫描器是否激活 */
  isActive: boolean
}

/**
 * Scanner 与 ExposureModule 交互的委托接口
 */
export interface ScannerDelegate {
  /** 发现新元素，通知 ExposureModule 注册观察 */
  onElementsDiscovered(elements: Element[]): void
  /** 元素已从 DOM 移除，通知 ExposureModule 取消观察 */
  onElementsRemoved(elements: Element[]): void
  /** 查询元素是否已被观察或已曝光 */
  isElementObserved(element: Element): boolean
  /** 获取当前被观察的元素数量 */
  getObservedCount(): number
  /** 重置所有曝光状态（路由变化时调用） */
  resetExposures(): void
}

/**
 * Scanner 配置
 */
export interface AutoExposureScannerConfig {
  /** 同时观察的最大元素数 */
  maxElements: number
  /** 扫描根节点 CSS 选择器，空字符串表示 document.body */
  scanRoot: string
  /** 调试模式 */
  debug: boolean
}

// ── 工具函数 ──

type AnyFunction = (...args: any[]) => void

interface TimerHandle {
  cancel(): void
}

/**
 * 创建节流函数（trailing 模式）
 */
function createThrottle(fn: AnyFunction, ms: number): AnyFunction & TimerHandle {
  let lastTime = 0
  let timer: ReturnType<typeof setTimeout> | null = null

  const throttled = (...args: any[]) => {
    const now = Date.now()
    const remaining = ms - (now - lastTime)

    if (remaining <= 0) {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      lastTime = now
      fn(...args)
    } else if (!timer) {
      // trailing: 确保最后一次调用也能触发
      timer = setTimeout(() => {
        lastTime = Date.now()
        timer = null
        fn(...args)
      }, remaining)
    }
  }

  throttled.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return throttled
}

/**
 * 创建防抖函数
 */
function createDebounce(fn: AnyFunction, ms: number): AnyFunction & TimerHandle {
  let timer: ReturnType<typeof setTimeout> | null = null

  const debounced = (...args: any[]) => {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn(...args)
    }, ms)
  }

  debounced.cancel = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  return debounced
}

// ── 核心类 ──

/**
 * 自动曝光扫描器
 */
export class AutoExposureScanner {
  private config: AutoExposureScannerConfig
  private delegate: ScannerDelegate

  /** 已发现的元素集合（O(1) 去重） */
  private scannedSet = new WeakSet<Element>()
  /** 可遍历的已追踪元素集合（用于 isConnected 清理） */
  private trackedElements = new Set<Element>()

  /** 是否为移动端设备 */
  private isMobile = false
  /** 全局冷却时间戳 */
  private lastScanTime = 0
  /** 防重入标志 */
  private isScanning = false
  /** 是否已启动 */
  private isStarted = false

  /** 累计扫描耗时（用于计算平均值） */
  private totalScanDuration = 0

  /** 性能指标 */
  private metrics: ScanMetrics = {
    totalScans: 0,
    totalElementsDiscovered: 0,
    currentObservedCount: 0,
    lastScanDuration: 0,
    avgScanDuration: 0,
    maxScanDuration: 0,
    scanTriggerCounts: {},
    droppedByThrottle: 0,
    droppedByCapacity: 0,
    removedByCleanup: 0,
    isActive: false,
  }

  // 事件处理器引用（用于 destroy 时移除）
  private throttledScrollHandler: (AnyFunction & TimerHandle) | null = null
  private debouncedResizeHandler: (AnyFunction & TimerHandle) | null = null
  private debouncedClickHandler: (AnyFunction & TimerHandle) | null = null
  private boundLoadHandler: (() => void) | null = null

  /** 路由变更管理器 */
  private routeChangeManager: RouteChangeManager
  /** 路由订阅取消函数 */
  private unsubscribeRouteChange: Unsubscribe | null = null
  /** 路由变化后的延迟扫描定时器 */
  private routeScanTimer: ReturnType<typeof setTimeout> | null = null

  constructor(config: AutoExposureScannerConfig, delegate: ScannerDelegate, routeChangeManager: RouteChangeManager) {
    this.config = config
    this.delegate = delegate
    this.routeChangeManager = routeChangeManager
  }

  /**
   * 启动扫描器
   */
  start(): void {
    if (this.isStarted) return
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    this.isStarted = true
    this.metrics.isActive = true

    // 检测设备类型
    this.isMobile = navigator.maxTouchPoints > 0 || 'ontouchstart' in window

    // 设置事件监听
    this.setupListeners()

    // 订阅路由变化
    this.unsubscribeRouteChange = this.routeChangeManager.subscribe(this.handleRouteChange)

    // 首次扫描
    this.scan('init')

    this.log('AutoExposureScanner 已启动', { isMobile: this.isMobile })
  }

  /**
   * 获取性能指标
   */
  getMetrics(): ScanMetrics {
    return {
      ...this.metrics,
      currentObservedCount: this.delegate.getObservedCount(),
    }
  }

  /**
   * 销毁扫描器
   */
  destroy(): void {
    if (!this.isStarted) return

    // 取消所有节流/防抖定时器
    this.throttledScrollHandler?.cancel()
    this.debouncedResizeHandler?.cancel()
    this.debouncedClickHandler?.cancel()

    // 取消路由变化后的延迟扫描定时器
    if (this.routeScanTimer) {
      clearTimeout(this.routeScanTimer)
      this.routeScanTimer = null
    }

    // 取消路由变化订阅
    this.unsubscribeRouteChange?.()
    this.unsubscribeRouteChange = null

    // 移除事件监听
    if (this.throttledScrollHandler) {
      window.removeEventListener('scroll', this.throttledScrollHandler)
    }
    if (this.debouncedResizeHandler) {
      window.removeEventListener('resize', this.debouncedResizeHandler)
    }
    if (this.debouncedClickHandler) {
      document.removeEventListener('click', this.debouncedClickHandler, true)
    }
    if (this.boundLoadHandler) {
      document.removeEventListener('DOMContentLoaded', this.boundLoadHandler)
      window.removeEventListener('load', this.boundLoadHandler)
    }

    // 清理状态
    this.trackedElements.clear()
    this.scannedSet = new WeakSet()
    this.isStarted = false
    this.metrics.isActive = false

    this.throttledScrollHandler = null
    this.debouncedResizeHandler = null
    this.debouncedClickHandler = null
    this.boundLoadHandler = null

    this.log('AutoExposureScanner 已销毁')
  }

  // ── 私有方法 ──

  /**
   * 注册事件监听
   */
  private setupListeners(): void {
    const defaults = AUTO_EXPOSURE_DEFAULTS

    const scrollMs = this.isMobile ? defaults.MOBILE_SCROLL_THROTTLE : defaults.PC_SCROLL_THROTTLE
    const clickMs = this.isMobile ? defaults.MOBILE_CLICK_DEBOUNCE : defaults.PC_CLICK_DEBOUNCE
    
    // 保存绑定的处理器引用，用于 destroy 时移除
    // 页面加载完成后延迟1秒触发扫描，确保异步渲染的元素已加载
    this.boundLoadHandler = () => setTimeout(() => this.scan('load'), 1000)
    // 页面加载
    if (document.readyState === 'complete') {
      // 页面已完全加载，延迟1秒后触发扫描
      setTimeout(() => this.scan('load'), 1000)
    } else if (document.readyState === 'interactive') {
      // DOM 已解析但资源未加载完，监听 load 事件
      window.addEventListener('load', this.boundLoadHandler)
    } else {
      // DOM 未解析完，同时监听 DOMContentLoaded 和 load
      document.addEventListener('DOMContentLoaded', this.boundLoadHandler)
      window.addEventListener('load', this.boundLoadHandler)
    }

    // scroll: 节流
    this.throttledScrollHandler = createThrottle(() => this.scan('scroll'), scrollMs)
    window.addEventListener('scroll', this.throttledScrollHandler, { passive: true })

    // resize: 防抖
    this.debouncedResizeHandler = createDebounce(() => this.scan('resize'), defaults.RESIZE_DEBOUNCE)
    window.addEventListener('resize', this.debouncedResizeHandler)

    // click: 防抖（capture 阶段）
    this.debouncedClickHandler = createDebounce(() => this.scan('click'), clickMs)
    document.addEventListener('click', this.debouncedClickHandler, true)
  }

  /**
   * 处理路由变化
   */
  private handleRouteChange = (_info: RouteChangeInfo): void => {
    // 取消上一次未执行的延迟扫描（快速切换路由时）
    if (this.routeScanTimer) {
      clearTimeout(this.routeScanTimer)
      this.routeScanTimer = null
    }

    // 路由变化：重置扫描状态
    this.scannedSet = new WeakSet()
    this.trackedElements.clear()

    // 通知 ExposureModule 重置曝光状态
    this.delegate.resetExposures()

    // 重置扫描次数计数（新页面重新开始计数）
    this.metrics.totalScans = 0
    this.totalScanDuration = 0

    this.log('Route changed, state reset', { url: _info.newUrl })

    // 延迟扫描新页面元素，等待 DOM 渲染完成
    this.routeScanTimer = setTimeout(() => {
      this.routeScanTimer = null
      this.scan('route')
    }, 1000)
  }

  /**
   * 核心扫描逻辑
   */
  private scan(trigger: 'init' | 'load' | 'scroll' | 'resize' | 'click' | 'route'): void {
    const now = Date.now()
    const cooldownMs = this.isMobile
      ? AUTO_EXPOSURE_DEFAULTS.MOBILE_COOLDOWN
      : AUTO_EXPOSURE_DEFAULTS.PC_COOLDOWN

    // 冷却检查（init 和 route 触发跳过冷却，确保初始化和路由变化时立即扫描）
    const skipCooldown = trigger === 'init' || trigger === 'route'
    if (!skipCooldown && now - this.lastScanTime < cooldownMs) {
      this.metrics.droppedByThrottle++
      return
    }

    // 防重入
    if (this.isScanning) return

    // 最大扫描次数检查
    if (this.metrics.totalScans >= AUTO_EXPOSURE_DEFAULTS.MAX_SCANS_PER_PAGE) {
      this.log('Max scans per page reached, skipping')
      return
    }

    this.isScanning = true
    this.lastScanTime = now
    const startTime = performance.now()

    try {
      // 解析扫描根节点
      const root = this.resolveScanRoot()
      if (!root) return

      // 清理阶段：检查已追踪元素是否仍在 DOM 中
      this.cleanupRemovedElements()

      // 扫描 DOM 查找新元素
      const candidates = root.querySelectorAll(`[${DATA_ATTRS.ELE_ID}]`)
      const newElements: Element[] = []

      for (let i = 0; i < candidates.length; i++) {
        const el = candidates[i]
        if (!this.scannedSet.has(el)) {
          newElements.push(el)
        }
      }

      if (newElements.length > 0) {
        // 容量检查
        const currentCount = this.delegate.getObservedCount()
        const available = this.config.maxElements - currentCount
        let toRegister: Element[]

        if (available <= 0) {
          this.metrics.droppedByCapacity += newElements.length
          toRegister = []
        } else if (newElements.length > available) {
          this.metrics.droppedByCapacity += newElements.length - available
          toRegister = newElements.slice(0, available)
        } else {
          toRegister = newElements
        }

        if (toRegister.length > 0) {
          // 通知 ExposureModule 注册
          this.delegate.onElementsDiscovered(toRegister)

          // 记录到已扫描集合
          for (const el of toRegister) {
            this.scannedSet.add(el)
            this.trackedElements.add(el)
          }

          this.metrics.totalElementsDiscovered += toRegister.length
        }
      }

      // 更新性能指标
      const duration = performance.now() - startTime
      this.metrics.totalScans++
      this.metrics.lastScanDuration = Math.round(duration * 100) / 100
      this.totalScanDuration += duration
      this.metrics.avgScanDuration = Math.round((this.totalScanDuration / this.metrics.totalScans) * 100) / 100
      if (duration > this.metrics.maxScanDuration) {
        this.metrics.maxScanDuration = Math.round(duration * 100) / 100
      }
      this.metrics.scanTriggerCounts[trigger] = (this.metrics.scanTriggerCounts[trigger] || 0) + 1
      if (newElements.length > 0) {
        this.log(`Scan [${trigger}]: found ${newElements.length} new elements in ${duration.toFixed(2)}ms`)
      }
    } finally {
      this.isScanning = false
    }
  }

  /**
   * 清理已从 DOM 移除的元素
   */
  private cleanupRemovedElements(): void {
    const removed: Element[] = []

    for (const el of this.trackedElements) {
      if (!el.isConnected) {
        removed.push(el)
      }
    }

    if (removed.length > 0) {
      // 通知 ExposureModule 取消观察
      this.delegate.onElementsRemoved(removed)

      // 从 trackedElements 移除（scannedSet 是 WeakSet，GC 时自动清理）
      for (const el of removed) {
        this.trackedElements.delete(el)
      }

      this.metrics.removedByCleanup += removed.length
      this.log(`Cleaned up ${removed.length} removed elements`)
    }
  }

  /**
   * 解析扫描根节点
   */
  private resolveScanRoot(): Element | null {
    if (this.config.scanRoot) {
      const root = document.querySelector(this.config.scanRoot)
      if (!root) {
        this.log('扫描根节点未找到，回退到 document.body', { selector: this.config.scanRoot })
      }
      return root || document.body
    }
    return document.body
  }

  /**
   * 调试
   */
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log('%c[Tracker AutoExposure 调试] %c ' + message, 'background: #67C23A; color: #fff;', 'color: inherit;', data)

    }
  }
}
